import { useState, useEffect, useCallback } from "react";
import { useGetProductsQuery } from "@entities/Shop/api/shopApiSlice";

interface Product {
  id: string;
  name: string;
  iconUrl: string;
  price: number;
  pricePyl: number;
  type: string;
  position: number;
  isAvailable: boolean;
  isPinned: boolean;
  rarity: string;
}

export const useProducts = () => {
  const [page, setPage] = useState(1);
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [hasMoreProducts, setHasMoreProducts] = useState(true);

  const { data, isLoading, error, isFetching } = useGetProductsQuery({
    page,
    pageSize: 20,
    sortBy: "price",
    sortDesc: true,
  }, {
    refetchOnMountOrArgChange: true,
    refetchOnReconnect: true,
  });

  // Reset products when component mounts or when critical dependencies change
  useEffect(() => {
    setPage(1);
    setAllProducts([]);
    setHasMoreProducts(true);
  }, []);

  // Update products list when data arrives
  useEffect(() => {
    if (data?.items) {
      console.log(`Received ${data.items.length} products for page ${page}`);

      // Check if we've reached the last page or received fewer items than requested
      if (data.items.length === 0 || (data.totalItems && allProducts.length >= data.totalItems)) {
        console.log("No more products available");
        setHasMoreProducts(false);
      }

      // Update the product list, excluding duplicates
      setAllProducts(prevProducts => {
        const newProducts = [...prevProducts];
        
        // Add only products that aren't already in the list
        data.items.forEach(product => {
          if (!newProducts.some(p => p.id === product.id)) {
            newProducts.push(product);
          }
        });

        return newProducts;
      });
    }
  }, [data, page]);

  // Memoize the load more function to prevent unnecessary re-renders
  const loadMore = useCallback(() => {
    if (!isLoading && !isFetching && hasMoreProducts) {
      console.log(`Loading more products, current page: ${page}, next page: ${page + 1}`);
      setPage(prevPage => prevPage + 1);
    }
  }, [isLoading, isFetching, hasMoreProducts, page]);

  // Map API data to component props
  const mappedProducts = allProducts.map(product => ({
    id: product.id,
    name: product.name,
    price: product.pricePyl,
    image: product.iconUrl,
    type: product.type === "archive_vip" ? "gold" : "silver",
    is_premium: product.type === "archive_vip",
    rarity: product.rarity || "Factory New",
  }));

  return {
    products: mappedProducts,
    loading: isLoading || isFetching,
    error: error ? "Произошла ошибка при загрузке продуктов" : null,
    hasMore: hasMoreProducts,
    loadMore,
    currentPage: page,
    totalProducts: data?.totalItems || allProducts.length,
    refresh: () => {
      setPage(1);
      setAllProducts([]);
    }
  };
};